import React from "react";
import PropTypes from "prop-types";
import { useFormContext, Controller } from "react-hook-form";
import { Loader2 } from "lucide-react"; // Standard loading icon

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";

const SelectInput = ({
  name,
  id,
  icon: Icon,
  label,
  placeholder = "Select...",
  value,
  onChange,
  options = [],
  required = false,
  className = "",
  classNameLabel = "",
  IconValue,
  wrapperClassName = "",
  rules,
  loading = false, // New prop
}) => {
  const formContext = useFormContext();
  const { t } = useTranslation();

  const renderOptions = () => (
    <SelectContent>
      {options.length > 0 ? (
        options.map((group) =>
          group.options ? (
            <SelectGroup key={group.groupLabel}>
              <SelectLabel className="text-sm font-bold text-secondary capitalize">
                {group.groupLabel}
              </SelectLabel>
              {group.options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="capitalize">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ) : (
            <SelectItem key={group.value} value={group.value}>
              {group.label}
            </SelectItem>
          )
        )
      ) : (
        <div className="p-4 text-center text-sm text-muted-foreground" role="status">
          {t("common.select.noOptions")}
        </div>
      )}
    </SelectContent>
  );

  const LabelComponent = (
    (label || Icon) && (
      <Label
        htmlFor={id}
        className={`mb-2 flex items-center text-secondary  capitalize ${classNameLabel}`}
      >
        {Icon && <Icon className="h-5 w-5 mx-1" />}
        {label}
        {required && <span className="text-red-500"> *</span>}
      </Label>
    )
  );

  /* -------------------- */
  /* NOT inside RHF form  */
  /* -------------------- */
  if (!formContext || !name) {
    return (
      <div className={wrapperClassName}>
        {LabelComponent}
        <Select value={value} onValueChange={onChange} disabled={loading}>
          <SelectTrigger id={id} className={`rounded-[1rem] capitalize ${className}`}>
            <div className="flex items-center gap-2">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : IconValue}
              <SelectValue placeholder={loading ? t("common.select.loading") : placeholder} />
            </div>
          </SelectTrigger>
          {!loading && renderOptions()}
        </Select>
      </div>
    );
  }

  /* -------------------- */
  /* Inside RHF form      */
  /* -------------------- */
  const { control } = formContext;

  return (
    <div className={wrapperClassName}>
      {LabelComponent}
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <>
            <Select
              value={field.value ?? ""}
              disabled={loading}
              onValueChange={(val) => {
                field.onChange(val);
                if (onChange) onChange(val);
              }}
            >
              <SelectTrigger
                id={id}
                className={`rounded-[1rem] capitalize ${className} ${
                  error ? "border border-destructive" : ""
                }`}
              >
                <div className="flex items-center gap-2">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : IconValue}
                  <SelectValue placeholder={loading ? t("common.select.loading") : placeholder} />
                </div>
              </SelectTrigger>
              {!loading && renderOptions()}
            </Select>
            {error && <p className="text-red-500 text-xs mt-1">{error.message}</p>}
          </>
        )}
      />
    </div>
  );
};

SelectInput.propTypes = {
  // ... existing props
  loading: PropTypes.bool,
  options: PropTypes.array,
};

export default SelectInput;